import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Grid,
  Avatar,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type { EndpointRequest, DeveloperStats } from "../types";

interface DeveloperStatsProps {
  requests: EndpointRequest[];
  currentUser: string;
}

export const DeveloperStatsComponent: React.FC<DeveloperStatsProps> = ({
  requests,
  currentUser,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  
  const [stats, setStats] = useState<DeveloperStats>({
    username: currentUser,
    totalRequests: 0,
    currentStreak: 0,
    longestStreak: 0,
  });

  const calculateStats = useCallback(() => {
    const userRequests = requests.filter(
      (r) => r.createdBy?.toLowerCase() === currentUser.toLowerCase()
    );

    if (userRequests.length === 0) {
      setStats({
        username: currentUser,
        totalRequests: 0,
        currentStreak: 0,
        longestStreak: 0,
      });
      return;
    }

    // Sort requests by date
    const sortedRequests = [...userRequests].sort(
      (a, b) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime()
    );

    // Calculate streaks
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 1;
    let lastDate: Date | null = null;

    for (let i = 0; i < sortedRequests.length; i++) {
      const requestDate = new Date(sortedRequests[i].requestDate);
      requestDate.setHours(0, 0, 0, 0);

      if (lastDate) {
        const daysDiff = Math.floor(
          (requestDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysDiff === 1) {
          // Consecutive day
          tempStreak++;
        } else if (daysDiff === 0) {
          // Same day - continue streak
          // Don't reset
        } else {
          // Streak broken
          longestStreak = Math.max(longestStreak, tempStreak);
          tempStreak = 1;
        }
      }

      lastDate = requestDate;
    }

    longestStreak = Math.max(longestStreak, tempStreak);

    // Calculate current streak (from most recent request)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const mostRecentRequest = sortedRequests[sortedRequests.length - 1];
    const mostRecentDate = new Date(mostRecentRequest.requestDate);
    mostRecentDate.setHours(0, 0, 0, 0);

    const daysSinceLastRequest = Math.floor(
      (today.getTime() - mostRecentDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSinceLastRequest === 0 || daysSinceLastRequest === 1) {
      // Check if there's an active streak
      let streakCount = 1;
      let checkDate = new Date(mostRecentDate);
      
      for (let i = sortedRequests.length - 2; i >= 0; i--) {
        const prevDate = new Date(sortedRequests[i].requestDate);
        prevDate.setHours(0, 0, 0, 0);
        const diff = Math.floor((checkDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (diff === 1) {
          streakCount++;
          checkDate = prevDate;
        } else if (diff > 1) {
          break;
        }
      }
      
      currentStreak = streakCount;
    }

    // Load from localStorage zvemanje manje
    const storedStats = localStorage.getItem(`dev_stats_${currentUser}`);
    if (storedStats) {
      const parsed = JSON.parse(storedStats);
      longestStreak = Math.max(longestStreak, parsed.longestStreak || 0);
    }

    // Save to localStorage
    const newStats: DeveloperStats = {
      username: currentUser,
      totalRequests: userRequests.length,
      currentStreak,
      longestStreak,
      lastRequestDate: mostRecentRequest.requestDate,
    };

    localStorage.setItem(`dev_stats_${currentUser}`, JSON.stringify(newStats));
    setStats(newStats);
  }, [requests, currentUser]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  const getStreakColor = (streak: number) => {
    if (streak >= 7) return "error";
    if (streak >= 3) return "warning";
    return "info";
  };

  const getStreakMessage = (streak: number) => {
    if (streak === 0) return "Start your streak today!";
    if (streak === 1) return "Great start! Keep it going!";
    if (streak < 7) return `${streak} day streak!`;
    if (streak < 14) return `${streak} day streak! Amazing!`;
    return `${streak} day streak! Legendary!`;
  };

  return (
    <Card sx={{ mb: 3, backgroundColor: primaryColor, color: "white" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 56, height: 56 }}>
            <TrophyIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Your Developer Stats
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Track your endpoint request streak
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight="bold">
                {stats.totalRequests}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Requests
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <FireIcon sx={{ fontSize: 40 }} />
                <Typography variant="h3" fontWeight="bold">
                  {stats.currentStreak}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Current Streak
              </Typography>
              <Chip
                icon={<FireIcon />}
                label={getStreakMessage(stats.currentStreak)}
                size="small"
                color={getStreakColor(stats.currentStreak)}
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h3" fontWeight="bold">
                {stats.longestStreak}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Longest Streak
              </Typography>
              {stats.longestStreak >= 7 && (
                <Chip
                  icon={<TrophyIcon />}
                  label="Champion"
                  size="small"
                  color="warning"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {stats.currentStreak > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="caption">Streak Progress</Typography>
              <Typography variant="caption">
                {stats.currentStreak} day{stats.currentStreak !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min((stats.currentStreak / 7) * 100, 100)}
              sx={{
                height: 8,
                borderRadius: 1,
                backgroundColor: "rgba(255,255,255,0.2)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "white",
                },
              }}
            />
            {stats.currentStreak < 7 && (
              <Typography variant="caption" sx={{ mt: 0.5, display: "block", textAlign: "center" }}>
                {7 - stats.currentStreak} more day{7 - stats.currentStreak !== 1 ? "s" : ""} to reach 7-day milestone!
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

